import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Contract } from "@edp-components/edp-contract";
import { EdpContractListComponent } from "@edp-components/edp-contract-list/edp-contract-list.component";
import { ContractDetails, ContractDetailsFactory, IContractDetails } from "@edp-components/edp-contract/edp-contract-details.model";
import {
    ContractFiltersFactory,
    ContractTariffsMapping,
    ContractedService,
    WallboxService,
} from "@edp-components/edp-contract/edp-contract.model";
import { EdpModalService } from "@edp-components/edp-modal";
import { BusinessAgrement, BusinessAgrementCollection } from "@edp-models/business-agrements.model";
import { Pack } from "@edp-components/edp-pack";
import { EdpToastType } from "@edp-components/edp-toaster/edp-toast-type";
import { EdpToasterService } from "@edp-components/edp-toaster/edp-toaster.service";
import { PartnerInfo } from "@edp-models/edp-partner-info.model";
import { PaymentReference } from "@edp-models/edp-payment-reference.model";
import { PendingContract } from "@edp-models/edp-pending-contract.model";
import { AddressResponse } from "@edp-models/postal-code.model";
import { ITotalDiscountsByContract } from "@edp-models/total-discount-contract.model";
import { ICheckUpCard } from "@edp-pages/service-funciona/review-check-up/review-check-up.model";
import { ContaCertaService } from "@edp-services/conta-certa.service";
import { ContractService } from "@edp-services/contract.service";
import { FuncionaService } from "@edp-services/funciona.service";
import { GoogleAnalyticsService } from "@edp-services/google-analytics.service";
import { PackService } from "@edp-services/pack.service";
import { EConsumptionActions } from "@edp-store/actions/consumption.actions";
import { EDashboardActions, SetCampaigns, SetFunciona } from "@edp-store/actions/dashboard.actions";
import { ELayoutActions, RefreshMainMenu, SetHeaderAlert, ShowLoader } from "@edp-store/actions/layout.actions";
import { ESmartNowActions } from "@edp-store/actions/smart-now.actions";
import { UserNotificationsGetAll } from "@edp-store/actions/user.actions";
import { contractsGetFilters, getContract, getContracts } from "@edp-store/selectors/contract.selectors";
import { getRouterState } from "@edp-store/selectors/router.selectors";
import { RouterStateUrl } from "@edp-utils/route-serializer";
import { Actions, Effect, ofType } from "@ngrx/effects";
import { RouterReducerState } from "@ngrx/router-store";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { EMPTY, of } from "rxjs";
import { catchError, map, mergeMap, switchMap, take, tap, withLatestFrom } from "rxjs/operators";
import {
    ContractDetailsAPIError,
    EContractActions,
    GetBusinessAgrements,
    GetAddressList,
    GetCheckupCards,
    GetContractDetails,
    GetContractFilters,
    GetDiscounts,
    GetPartnerInfo,
    GetPaymentReference,
    GetTariffsMappings,
    IsSingleContract,
    PartnerInfoAPIError,
    SelectContract,
    SetBusinessAgrements,
    SetAddressList,
    SetCheckupCards,
    SetContractChanges,
    SetContractDetails,
    SetContractFilters,
    SetContracts,
    SetDiscounts,
    SetPacks,
    SetPartnerInfo,
    SetPaymentReference,
    SetPendingContracts,
    SetTariffsMappings,
    SetTotalDiscount,
    SetTotalDiscountsByContract,
    ShowContractSelection,
    GetAlerts,
    GetContractedServices,
    GetWallBoxService,
    GetPacks,
    GetContracts,
    UpdateBusinessAgrementsStatus,
    SetPaymentReferenceError,
} from "../actions/contract.actions";
import { IAppState } from "../state/app.state";
import { ServiceService } from "@edp-services/service.service";
import { RegionService } from "@edp-services/region.service";
import { RegionEnum } from "@edp-enums/region.enum";
import { MeddaliaTrackingService } from "@edp-services/meddalia-tracking.service";
import { GetHouseStatusMoonShot } from "@edp-store/actions/define-house.action";

@Injectable()
export class ContractEffects {
    @Effect()
    loadContracts$ = this.actions$.pipe(
        ofType(EContractActions.GetContracts),
        tap(() => this.store.dispatch(new ShowLoader(true))),
        switchMap((action: GetContracts) => this.contractService.getList(action.forceSync)),
        withLatestFrom(this.store.select(getRouterState)),
        mergeMap(([contracts, router]: [Contract[], RouterReducerState<RouterStateUrl>]) => {
            if (this.regionService.getRegion() !== RegionEnum.PT) {
                return of(new ShowLoader(false));
            }
            const contractList: Contract[] = contracts.map(contract => this.contractService.createContract(contract));
            let contract: Contract;

            if (router.state.params.contract_id) {
                contract = contractList.find(contract => contract.ContractId === router.state.params.contract_id);
            }
            if (router.state.queryParams.contract_id) {
                contract = contractList.find(contract => contract.ContractId === router.state.queryParams.contract_id);
            }

            if (!contract) {
                const activeContracts = contractList.filter(contract => contract.isActivo());
                if (activeContracts.length === 1 || contractList.length === 1) {
                    contract = contractList.length === 1 ? contractList[0] : activeContracts[0];
                }
            }

            if (contract) {
                this.store.dispatch(new SelectContract(contract));
                this.store.dispatch(new GetHouseStatusMoonShot(true));
            }

            if (contractList.length > 1) {
                this.store.dispatch(new IsSingleContract(false));
            }

            if (!contract && !router.state.data.skipContractSelection) {
                this.store.dispatch(new ShowContractSelection());
            }

            return of(new SetContracts(contractList), new UserNotificationsGetAll(), new ShowLoader(false), new GetContractFilters());
        })
    );

    @Effect()
    loadPendingContracts$ = this.actions$.pipe(
        ofType(EContractActions.GetPendingContracts),
        switchMap(() => this.contractService.getPendingContracts()),
        switchMap((pendingContracts: PendingContract[]) => {
            return of(new SetPendingContracts(pendingContracts));
        })
    );

    @Effect()
    selectContract$ = this.actions$.pipe(
        ofType(EContractActions.SelectContract),
        switchMap((action: SelectContract) => {
            if (this.regionService.getRegion() !== RegionEnum.PT) {
                return of(new ShowLoader(false));
            }
            this.googleAnalyticsService.pageChangeEvent();
            this.store.dispatch(new RefreshMainMenu());
            this.store.dispatch(new SetCampaigns(null));
            const actions = [
                {
                    type: EConsumptionActions.SetConsumptions,
                    payload: null,
                },
                {
                    type: EContractActions.ClearSelectedContractData,
                },
                {
                    type: EDashboardActions.ClearSelectedContractDataInDashboard,
                },
                {
                    type: EDashboardActions.GetBalance,
                    contract: action.contract,
                },
                {
                    type: EContractActions.GetPartnerInfo,
                    contract: action.contract,
                },
                {
                    type: EDashboardActions.GetActiveMeters,
                    contract: action.contract,
                },
                {
                    type: EContractActions.GetContractDetails,
                    contract: action.contract,
                },
                {
                    type: EDashboardActions.GetContractRequests,
                    contract: action.contract,
                },
                {
                    type: EConsumptionActions.GetConsumptions,
                    contract: action.contract,
                },
                {
                    type: ESmartNowActions.SetSmartNowStatus,
                    payload: { status: 0, lastCommunication: null },
                },
            ];

            let mobieActions = [];
            if (action.contract.HasM) {
                mobieActions = [
                    {
                        type: EDashboardActions.SetElectricMobilityWidgetData,
                        payload: null,
                    },
                    {
                        type: EDashboardActions.GetElectricMobilityWidgetData,
                        contractId: action.contract.ContractId,
                    },
                ];
            }

            let smartNowActions = [];
            if (action.contract && action.contract.SmartNow && this.router.url.indexOf("consumos") === -1) {
                smartNowActions = [
                    {
                        type: ESmartNowActions.SetSmartNowStatus,
                        payload: null,
                    },
                    {
                        type: ESmartNowActions.GetSmartNowStatus,
                        contract: action.contract,
                    },
                    {
                        type: ESmartNowActions.GetConsumptionWithDisaggregation,
                        payload: { contractId: action.contract.ContractId },
                    },
                    {
                        type: ESmartNowActions.GetConsumptionPrediction,
                        contractId: action.contract.ContractId,
                    },
                ];
            }

            return [...actions, ...mobieActions, ...smartNowActions];
        })
    );

    @Effect()
    loadContractDetail$ = this.actions$.pipe(
        ofType(EContractActions.GetContractDetails),
        switchMap((action: GetContractDetails) =>
            this.contractService.getDetails(action.contract).pipe(catchError((error: Error) => of(new ContractDetailsAPIError(error))))
        ),
        switchMap((contractDetails: IContractDetails | ContractDetailsAPIError) => {
            if (contractDetails instanceof ContractDetailsAPIError) {
                return of(contractDetails);
            } else {
                const details: ContractDetails = ContractDetailsFactory.create(contractDetails);

                // const power = details.Contracts[0].BasicComponent[0].Power;

                return of(new SetFunciona(details.getFunciona()), new SetContractDetails(details));
            }
        })
    );

    @Effect()
    loadPartnerInfo$ = this.actions$.pipe(
        ofType(EContractActions.GetPartnerInfo),
        switchMap((action: GetPartnerInfo) =>
            this.contractService.getPartnerInfo(action.contract).pipe(
                // switch to error action
                catchError((error: Error) => of(new PartnerInfoAPIError(error)))
            )
        ),
        switchMap((partnerInfo: PartnerInfo | PartnerInfoAPIError) => {
            if (partnerInfo instanceof PartnerInfoAPIError) {
                return of(partnerInfo);
            } else {
                if (partnerInfo && partnerInfo.partnerships) {
                    partnerInfo.partnerships.forEach(partner => {
                        const partnerName = partner.name.replace(/\s/g, "").toLowerCase();
                        partner.partnerName = `pt.edp.edponline.pack.leisure.knowmore.${partnerName}`;
                    });
                }

                return of(new SetPartnerInfo(partnerInfo), new SetTotalDiscount(partnerInfo.totalDiscounts.value));
            }
        })
    );

    @Effect()
    loadPaymentReference$ = this.actions$.pipe(
        ofType(EContractActions.GetPaymentReference),
        switchMap((action: GetPaymentReference) =>
            this.contractService
                .getPaymentReference(action.contract)
                .pipe(catchError((error: Error) => of(new SetPaymentReferenceError(error))))
        ),
        switchMap((paymentReference: PaymentReference | SetPaymentReferenceError) => {
            if (paymentReference instanceof SetPaymentReferenceError) {
                return of(paymentReference);
            }
            return of(new SetPaymentReference(paymentReference));
        })
    );

    @Effect()
    loadTotalDiscountsByContract$ = this.actions$.pipe(
        ofType(EContractActions.GetTotalDiscountsByContract),
        switchMap(() => this.contractService.getTotalDiscountsByContract()),
        withLatestFrom(this.store.select(getContracts)),
        mergeMap(([totalDiscountsByContract, contracts]: [ITotalDiscountsByContract, Contract[]]) => {
            if (totalDiscountsByContract && totalDiscountsByContract.creditAmount) {
                totalDiscountsByContract.creditAmount.forEach(creditAmount => {
                    creditAmount.contract = contracts.find(contract => contract.ContractId === creditAmount.contractId);
                });
            }

            return of(new SetTotalDiscountsByContract(totalDiscountsByContract));
        })
    );

    @Effect({ dispatch: false })
    setActiveContractRatingFlag$ = this.actions$.pipe(
        ofType(EContractActions.FlagContractRating),
        tap(() => {
            this.store.pipe(select(getContract), take(1)).subscribe(contract => {
                // Temporary iframe rating
                this.syncIframeRating(contract);
            });
        })
    );

    @Effect()
    loadComplementaryInfo$ = this.actions$.pipe(
        ofType(EContractActions.GetComplementaryInfo),
        mergeMap(() =>
            this.contractService.getComplementaryInfo().pipe(
                map(response => ({ type: EContractActions.SetComplementaryInfo, payload: response })),
                catchError(() => EMPTY)
            )
        )
    );

    @Effect()
    sendComplementaryInfoForm$ = this.actions$.pipe(
        ofType(EContractActions.SendComplementaryInfoForm),
        switchMap((payload: any) => {
            return this.contractService.sendComplementaryInfoForm(payload.payload).pipe(
                switchMap(() => {
                    this.toaster
                        .create(this.translate.instant("pt.edp.edponline.defineHouse.toaster.info"), EdpToastType.info, {
                            timeout: 2000,
                        })
                        .show();
                    return of({ type: EContractActions.GetComplementaryInfo });
                }),
                catchError(() => EMPTY)
            );
        })
    );

    @Effect({ dispatch: false })
    showContractSelection$ = this.actions$.pipe(
        ofType(EContractActions.ShowContractSelection),
        tap(() => {
            if (this.regionService.getRegion() !== RegionEnum.PT) {
                return of(null);
            }
            const { options, modal } = this.contractService.getFullModalParam(false);
            this.edpModalService.custom(EdpContractListComponent, options, modal).then(() => {
                /* Intentionally silent */
            });
        })
    );

    @Effect({ dispatch: false })
    hideContractSelection$ = this.actions$.pipe(
        ofType(EContractActions.HideContractSelection),
        tap(() => {
            if (this.edpModalService.hasInstance().length) {
                this.edpModalService.removeAll();
            }
        })
    );

    @Effect()
    selectFilters$ = this.actions$.pipe(
        ofType(EContractActions.GetContractFilters),
        withLatestFrom(this.store.select(contractsGetFilters).pipe(take(1))),
        mergeMap(([action, contracts]) => {
            if (contracts?.length === 0) {
                return of(new SetContractFilters(ContractFiltersFactory.generateContractFilters()));
            }
            return EMPTY;
        })
    );

    @Effect()
    getCheckupCards$ = this.actions$.pipe(
        ofType(EContractActions.GetCheckupCards),
        switchMap((action: GetCheckupCards) => this.funcionaService.getCheckupCards(action.contract)),
        switchMap((checkUpCards: ICheckUpCard[]) => {
            const cardList: ICheckUpCard[] = checkUpCards.map(card => {
                card.selected = false;
                return card;
            });
            return of(new SetCheckupCards(cardList));
        })
    );

    @Effect()
    getBusinessAgrements$ = this.actions$.pipe(
        ofType(EContractActions.GetBusinessAgrements),
        switchMap((action: GetBusinessAgrements) =>
            this.contractService.getBusinessAgrements(action.payload).pipe(
                catchError(() => {
                    this.store.dispatch(new ShowLoader(false));
                    return EMPTY;
                })
            )
        ),
        switchMap((businessAgrements: BusinessAgrementCollection) => {
            this.store.dispatch(new ShowLoader(false));

            if (businessAgrements) {
                return of(new SetBusinessAgrements(businessAgrements));
            }
            return EMPTY;
        })
    );

    @Effect()
    updateBusinessAgrements$ = this.actions$.pipe(
        ofType(EContractActions.UdpateBusinessAgrements),
        switchMap((payload: any) => {
            this.store.dispatch(new ShowLoader(true));
            return this.contractService.updateBusinessAgrements(payload.payload).pipe(
                mergeMap(() => {
                    return [
                        { type: EContractActions.GetBusinessAgrements, payload: { contractId: payload.payload.contractId } },
                        { type: EContractActions.UpdateBusinessAgrementsStatus, status: { error: null, status: true } },
                        { type: ELayoutActions.ShowLoader, payload: false },
                    ];
                }),
                catchError(error => [
                    { type: EContractActions.UpdateBusinessAgrementsStatus, status: { error, status: false } },
                    { type: ELayoutActions.ShowLoader, payload: false },
                ])
            );
        })
    );

    @Effect()
    setContractChanges$ = this.actions$.pipe(
        ofType(EContractActions.SetContractChanges),
        switchMap((action: SetContractChanges) => {
            this.store.dispatch(new ShowLoader(true));
            return this.contractService.updateContract(action.payload).pipe(
                mergeMap(() => {
                    this.setMedalliaByContractOperation(action.payload.Operation);
                    return [{ type: ELayoutActions.ShowLoader, payload: false }, { type: EContractActions.GetContracts }];
                }),
                catchError(() => [{ type: ELayoutActions.ShowLoader, payload: false }])
            );
        })
    );

    @Effect()
    getAddressList$ = this.actions$.pipe(
        ofType(EContractActions.GetAddressList),
        switchMap((action: GetAddressList) => this.contractService.getAddressList(action.payload)),
        switchMap((response: AddressResponse[]) => {
            const addresses = !!response[0] ? response[0]?.addresses : [];
            return of(new SetAddressList(addresses));
        })
    );

    @Effect()
    getTariffsMapping$ = this.actions$.pipe(
        ofType(EContractActions.GetTariffsMappings),
        switchMap(() => this.contractService.getTariffsMappings()),
        switchMap((tariffsMapping: ContractTariffsMapping) => {
            return of(new SetTariffsMappings(tariffsMapping));
        })
    );

    @Effect()
    getPacks$ = this.actions$.pipe(
        ofType(EContractActions.GetPacks),
        switchMap((action: GetPacks) => {
            const args: { rule: string; contract_id?: string } = { rule: "packs" };
            args.contract_id = args.contract_id ?? action.contract_id;
            const getByCache = !args.contract_id;
            return this.packService.getAll({ ...args }, getByCache);
        }),
        switchMap((packs: Pack[]) => {
            return of(new SetPacks(packs));
        })
    );

    @Effect()
    getDiscounts$ = this.actions$.pipe(
        ofType(EContractActions.GetDiscounts),
        switchMap((action: GetDiscounts) => this.contractService.getDiscounts(action.payload)),
        switchMap((discounts: any) => {
            return of(new SetDiscounts(discounts));
        })
    );

    @Effect()
    getContractedServices$ = this.actions$.pipe(
        ofType(EContractActions.GetContractedServices),
        switchMap((action: GetContractedServices) => {
            return this.serviceService.getContractedServices(action.payload).pipe(
                switchMap((response: ContractedService[]) => [{ type: EContractActions.SetContractedServices, payload: response }]),
                catchError(() => [{ type: EContractActions.SetContractedServices, payload: null }])
            );
        })
    );

    @Effect()
    getWallBoxService$ = this.actions$.pipe(
        ofType(EContractActions.GetWallBoxService),
        switchMap((action: GetWallBoxService) => {
            return this.contractService.getWallboxService(action.payload).pipe(
                switchMap((response: WallboxService) => [{ type: EContractActions.SetWallBoxService, payload: response }]),
                catchError(() => [{ type: EContractActions.SetWallBoxService, payload: null }])
            );
        })
    );

    @Effect()
    getAlerts$ = this.actions$.pipe(
        ofType(EContractActions.GetAlerts),
        switchMap((action: GetAlerts) => {
            return this.contractService.getAlerts(action.payload).pipe(
                switchMap(response => [{ type: EContractActions.SetAlerts, payload: response }]),
                catchError(() => EMPTY)
            );
        })
    );

    constructor(
        private actions$: Actions,
        private contractService: ContractService,
        private store: Store<IAppState>,
        private edpModalService: EdpModalService,
        private googleAnalyticsService: GoogleAnalyticsService,
        private funcionaService: FuncionaService,
        private router: Router,
        private translate: TranslateService,
        private toaster: EdpToasterService,
        private contaCertaService: ContaCertaService,
        private packService: PackService,
        private serviceService: ServiceService,
        private regionService: RegionService,
        private meddaliaTrackingService: MeddaliaTrackingService
    ) {}

    private syncIframeRating(contract: Contract): void {
        const iframeRating = sessionStorage.getItem("iframe_rating");
        let iframeContracts: string[] = [];

        if (iframeRating) {
            iframeContracts = JSON.parse(iframeRating) ?? [];
        }

        if (!iframeContracts.find(contractId => contractId === contract.ContractId)) {
            iframeContracts.push(contract.ContractId);
        }

        sessionStorage.setItem("iframe_rating", JSON.stringify(iframeContracts));
    }

    private setMedalliaByContractOperation(operation: string) {
        switch (operation) {
            case "CHANGE_CYCLE_POWER_TARIFF":
                this.meddaliaTrackingService.setMedalliaTracking("Alteração de potência");
                break;
            case "CHANGE_INVOICE_PERIODICITY":
                this.meddaliaTrackingService.setMedalliaTracking("Alteração de periodicidade da fatura");
                break;
            case "CHANGE_PAYMENT_CONDITION":
                this.meddaliaTrackingService.setMedalliaTracking("Alteração de condições de pagamentos");
                break;
            case "CHANGE_INVOICE_ADDRESS":
                this.meddaliaTrackingService.setMedalliaTracking("Alteração de morada de envio de fatura");
                break;
            case "CHANGE_INVOICE_EMAIL":
                this.meddaliaTrackingService.setMedalliaTracking("Alteração de email de fatura eletrónica");
                break;
            case "CHANGE_IBAN_NUMBER":
                this.meddaliaTrackingService.setMedalliaTracking("Alteração de IBAN");
                break;
            case "CHANGE_PHONE_NUMBER":
                this.meddaliaTrackingService.setMedalliaTracking("Alteração de telefone");
                break;
        }
    }
}
